import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { tap, catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  GitHubConnectionInfo,
  GitHubTokenRequest,
  GitHubTokenResponse,
} from '../models/github.models';
import { PlatformPortalApiService } from './platform-portal-api.service';
import { NavigationService } from './navigation.service';
import { SecureCookieService } from './secure-cookie.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class GitHubAuthService {
  private readonly STORAGE_KEYS = {
    CONNECTION_STATE: 'github_connection_state',
    CODE_VERIFIER: 'github_code_verifier',
    STATE: 'github_state',
    RETURN_URL: 'github_return_url',
  };

  private readonly COOKIE_NAMES = {
    ACCESS_TOKEN: 'gh_access_token',
    REFRESH_TOKEN: 'gh_refresh_token',
    TOKEN_EXPIRES: 'gh_token_expires',
  } as const;

  private readonly connectionInfo = new BehaviorSubject<GitHubConnectionInfo>({
    isConnected: false,
    lastSync: null,
    error: null,
  });

  public connectionInfo$ = this.connectionInfo.asObservable();

  private tokenValidationTimer: any = null;
  private readonly TOKEN_VALIDATION_INTERVAL = 30 * 60 * 1000; // 30 minutes

  constructor(
    private readonly platformPortalApiService: PlatformPortalApiService,
    private readonly router: Router,
    private readonly navigationService: NavigationService,
    private readonly secureCookieService: SecureCookieService,
    private readonly injector: Injector,
    private readonly toast: ToastService
  ) {
    this.initializeConnectionState();
    this.scheduleTokenValidation();
  }

  /**
   * Gets the GitHub access token from cookies with expiration checking
   */
  private getGitHubAccessToken(): string | null {
    if (this.isTokenExpiredByCookie()) {
      this.clearAllGitHubTokens();
      return null;
    }
    return this.secureCookieService.getCookie(this.COOKIE_NAMES.ACCESS_TOKEN);
  }

  /**
   * Sets the GitHub access token in a secure cookie with expiration tracking
   */
  private setGitHubAccessToken(token: string): void {
    this.secureCookieService.setCookieWithExpirationTracking(
      this.COOKIE_NAMES.ACCESS_TOKEN,
      token,
      this.COOKIE_NAMES.TOKEN_EXPIRES
    );
  }

  /**
   * Gets the GitHub refresh token from cookies
   */
  private getGitHubRefreshToken(): string | null {
    if (this.isTokenExpiredByCookie()) {
      this.clearAllGitHubTokens();
      return null;
    }
    return this.secureCookieService.getCookie(this.COOKIE_NAMES.REFRESH_TOKEN);
  }

  /**
   * Sets the GitHub refresh token in a secure cookie
   */
  private setGitHubRefreshToken(token: string): void {
    this.secureCookieService.setCookie(this.COOKIE_NAMES.REFRESH_TOKEN, token);
  }

  /**
   * Gets the GitHub connection info from cookies and decodes from base64
   */
  private getGitHubConnectionInfo(): string | null {
    const encodedValue = this.secureCookieService.getCookie('gh_connection_info');
    if (!encodedValue) {
      return null;
    }

    try {
      return atob(encodedValue);
    } catch (error) {
      console.warn('Failed to decode connection info as base64:', error);
      return null;
    }
  }

  /**
   * Clears all GitHub-related tokens and cookies
   */
  private clearAllGitHubTokens(): void {
    const cookieNames = Object.values(this.COOKIE_NAMES);
    this.secureCookieService.clearCookies(cookieNames);
    this.secureCookieService.clearCookies(['gh_connection_info']);
  }

  /**
   * Checks if token has expired based on the expiration cookie
   */
  private isTokenExpiredByCookie(): boolean {
    return this.secureCookieService.isCookieExpired(this.COOKIE_NAMES.TOKEN_EXPIRES);
  }

  private initializeConnectionState(): void {
    const storedInfo = this.getGitHubConnectionInfo();
    const accessToken = this.getGitHubAccessToken();

    if (accessToken && storedInfo) {
      try {
        const parsedInfo = JSON.parse(storedInfo);
        const info: GitHubConnectionInfo = {
          isConnected: parsedInfo.isConnected || false,
          lastSync: parsedInfo.lastSync ? new Date(parsedInfo.lastSync) : null,
          error: parsedInfo.error || null,
        };
        this.connectionInfo.next(info);
        console.log('[GitHubAuthService] Restored connection info from cookies:', info);
      } catch (error) {
        console.error('Error parsing stored GitHub connection info:', error);
      }
    }
  }

  /**
   * Generates a random string for PKCE code verifier
   */
  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Generates SHA256 hash for PKCE code challenge
   */
  private generateCodeChallenge(verifier: string): Observable<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);

    return from(crypto.subtle.digest('SHA-256', data)).pipe(
      map(digest =>
        btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=/g, '')
      )
    );
  }

  /**
   * Generates a random state parameter
   */
  private generateState(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode.apply(null, Array.from(array)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Stores the current route to return to after authentication
   */
  public storeReturnUrl(url: string): void {
    // Don't store the callback route itself or empty/invalid URLs
    if (url && !url.includes('/auth/github/callback')) {
      sessionStorage.setItem(this.STORAGE_KEYS.RETURN_URL, url);
    }
  }

  /**
   * Gets and clears the stored return URL
   */
  public getAndClearReturnUrl(): string | null {
    const returnUrl = sessionStorage.getItem(this.STORAGE_KEYS.RETURN_URL);
    if (returnUrl) {
      sessionStorage.removeItem(this.STORAGE_KEYS.RETURN_URL);
      return returnUrl;
    }
    return null;
  }

  private popupWindow: Window | null = null;
  private popupCheckInterval: any = null;

  /**
   * Initiates the GitHub OAuth flow with PKCE using popup window
   */
  public loginWithGitHub(): Observable<void> {
    const codeVerifier = this.generateCodeVerifier();
    const state = this.generateState();

    return this.generateCodeChallenge(codeVerifier).pipe(
      switchMap(codeChallenge => {
        try {
          // Store PKCE parameters
          sessionStorage.setItem(this.STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
          sessionStorage.setItem(this.STORAGE_KEYS.STATE, state);

          const params = new URLSearchParams({
            client_id: environment.github.clientId,
            redirect_uri: `${window.location.origin}/assets/github-popup.html`,
            scope: 'read:user user:email read:org repo',
            response_type: 'code',
            state: state,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
          });

          const authUrl = `https://github.com/login/oauth/authorize?${params.toString()}`;

          return this.openAuthPopup(authUrl);
        } catch (error) {
          throw new Error(`Failed to store OAuth parameters: ${error}`);
        }
      }),
      catchError(error => {
        console.error('Error initiating GitHub OAuth flow:', error);
        this.updateConnectionInfo({
          error: 'Failed to initiate GitHub authentication',
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Opens the OAuth popup and handles the authentication flow
   */
  private openAuthPopup(authUrl: string): Observable<void> {
    return new Observable<void>(observer => {
      // Close any existing popup
      this.closePopup();

      // Open new popup
      this.popupWindow = this.navigationService.openPopup(
        authUrl,
        'github_oauth',
        600,
        700
      );

      // Enhanced popup blocking detection
      if (!this.popupWindow) {
        this.handlePopupBlocked(observer);
        return;
      }

      // Additional check for popup blocking - some browsers return a window object but it's immediately closed
      setTimeout(() => {
        if (this.popupWindow && this.popupWindow.closed) {
          this.handlePopupBlocked(observer);
          return;
        }
      }, 1000);

      // Set up postMessage listener
      const messageListener = (event: MessageEvent) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        if (event.data?.type === 'GITHUB_OAUTH_RESULT') {
          window.removeEventListener('message', messageListener);
          this.clearPopupCheckInterval();

          if (event.data.success && event.data.code) {
            // Handle successful OAuth callback
            this.handlePopupCallback(event.data.code, event.data.state)
              .subscribe({
                next: () => observer.next(),
                error: (error) => observer.error(error),
                complete: () => observer.complete()
              });
          } else {
            const errorMsg = event.data.error || 'Authentication failed';
            observer.error(new Error(`GitHub OAuth error: ${errorMsg}`));
          }
        }
      };

      // Listen for messages from popup
      window.addEventListener('message', messageListener);

      // Check if popup was closed manually
      this.popupCheckInterval = setInterval(() => {
        if (this.popupWindow?.closed) {
          window.removeEventListener('message', messageListener);
          this.clearPopupCheckInterval();
          observer.error(new Error('Authentication was cancelled'));
        }
      }, 1000);

      // Cleanup function
      return () => {
        window.removeEventListener('message', messageListener);
        this.closePopup();
      };
    });
  }

  /**
   * Handles the OAuth callback from popup
   */
  private handlePopupCallback(code: string, state: string): Observable<void> {
    const storedState = sessionStorage.getItem(this.STORAGE_KEYS.STATE);
    if (!storedState || state !== storedState) {
      return throwError(() => new Error('Invalid state parameter'));
    }

    const codeVerifier = sessionStorage.getItem(this.STORAGE_KEYS.CODE_VERIFIER);
    if (!codeVerifier) {
      return throwError(() => new Error('No code verifier found'));
    }

    return this.exchangeCodeForToken(
      code,
      codeVerifier,
      `${window.location.origin}/assets/github-popup.html`
    );
  }

  /**
   * Exchanges authorization code for access token
   * Shared logic used by both popup and redirect OAuth flows
   */
  private exchangeCodeForToken(
    code: string,
    codeVerifier: string,
    redirectUri: string
  ): Observable<void> {
    const githubTokenRequest: GitHubTokenRequest = {
      Code: code,
      CodeVerifier: codeVerifier,
      RedirectUri: redirectUri,
    };

    return this.platformPortalApiService.getGithubToken(githubTokenRequest).pipe(
      tap((data: GitHubTokenResponse) => {
        if (!data) {
          throw new Error('Token exchange failed');
        }

        if (data.accessToken) {
          this.setGitHubAccessToken(data.accessToken);

          if (data.refreshToken) {
            this.setGitHubRefreshToken(data.refreshToken);
          }

          this.updateConnectionInfo({
            isConnected: true,
            error: null,
            lastSync: new Date(),
          });

          // Clean up PKCE parameters
          sessionStorage.removeItem(this.STORAGE_KEYS.CODE_VERIFIER);
          sessionStorage.removeItem(this.STORAGE_KEYS.STATE);
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.error('Error handling GitHub OAuth callback:', error);
        this.updateConnectionInfo({
          error: 'Failed to complete GitHub authentication',
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Closes the popup window and clears intervals
   */
  private closePopup(): void {
    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close();
    }
    this.popupWindow = null;
    this.clearPopupCheckInterval();
  }

  /**
   * Clears the popup check interval
   */
  private clearPopupCheckInterval(): void {
    if (this.popupCheckInterval) {
      clearInterval(this.popupCheckInterval);
      this.popupCheckInterval = null;
    }
  }

  /**
   * Handles popup blocking scenarios
   */
  private handlePopupBlocked(observer: any): void {
    console.warn('GitHub authentication popup was blocked');

    // Clear the interval to prevent duplicate error emission
    this.clearPopupCheckInterval();

    // Update connection info with popup blocked error
    this.updateConnectionInfo({
      error: 'GitHub authentication popup was blocked. Please allow popups for this site and try again.',
    });

    // Show toast notification to user
    this.toast.error(
      'Popup blocked! Please allow popups for this site and try connecting again.',
      'Popup Blocked',
      8000 // Show for 8 seconds since it's an important message
    );

    // Create a user-friendly error with instructions
    const popupBlockedError = new Error(
      'Popup blocked! To connect to GitHub:\n\n' +
      '1. Look for the popup blocker icon in your browser\'s address bar\n' +
      '2. Click it and select "Always allow popups from this site"\n' +
      '3. Try connecting to GitHub again'
    );

    // Add a property to identify this as a popup blocked error
    (popupBlockedError as any).isPopupBlocked = true;

    observer.error(popupBlockedError);
  }


  /**
   * Handles the OAuth callback and exchanges code for access token via backend
   */
  public handleCallback(code?: string, state?: string, error?: string): Observable<void> {
    // If parameters aren't provided, fall back to parsing from URL (for backward compatibility)
    if (code === undefined || state === undefined || error === undefined) {
      const urlParams = new URLSearchParams(window.location.search);
      code = code ?? urlParams.get('code') ?? undefined;
      state = state ?? urlParams.get('state') ?? undefined;
      error = error ?? urlParams.get('error') ?? undefined;
    }

    if (error) {
      return throwError(() => new Error(`GitHub OAuth error: ${error}`));
    }

    if (!code) {
      return throwError(() => new Error('No authorization code received'));
    }

    const storedState = sessionStorage.getItem(this.STORAGE_KEYS.STATE);
    if (!storedState || state !== storedState) {
      return throwError(() => new Error('Invalid state parameter'));
    }

    const codeVerifier = sessionStorage.getItem(this.STORAGE_KEYS.CODE_VERIFIER);
    if (!codeVerifier) {
      return throwError(() => new Error('No code verifier found'));
    }

    return this.exchangeCodeForToken(
      code,
      codeVerifier,
      `${window.location.origin}/auth/github/callback`
    );
  }

  /**
   * Logs out from GitHub and clears stored data
   */
  public logout(): void {
    this.clearStoredData();
    this.clearAllGitHubTokens();

    // Reset connection info
    this.connectionInfo.next({
      isConnected: false,
      lastSync: null,
      error: null,
    });
  }

  /**
   * Gets the current access token
   */
  public getAccessToken(): string | null {
    return this.getGitHubAccessToken();
  }

  /**
   * Checks if user is currently connected to GitHub
   */
  public isConnected(): boolean {
    return this.connectionInfo.value.isConnected && !!this.getAccessToken();
  }

  /**
   * Updates the lightweight connection info (separates connection status from user data)
   */
  public updateConnectionInfo(updates: Partial<GitHubConnectionInfo>): void {
    const currentInfo = this.connectionInfo.value;
    const newInfo = { ...currentInfo, ...updates };

    // Store lightweight info to secure cookies
    try {
      const infoJson = JSON.stringify(newInfo);
      const encodedInfo = btoa(infoJson);
      this.secureCookieService.setCookie('gh_connection_info', encodedInfo);
    } catch (error) {
      console.warn('Failed to save GitHub connection info to cookies:', error);
    }

    this.connectionInfo.next(newInfo);
  }

  /**
   * Checks if the current token is expired based on cookie expiration tracking
   */
  public isTokenExpired(): boolean {
    return this.isTokenExpiredByCookie();
  }

  /**
   * Schedules periodic token validation
   */
  private scheduleTokenValidation(): void {
    // Clear any existing timer
    if (this.tokenValidationTimer) {
      clearInterval(this.tokenValidationTimer);
    }

    // Set up new timer for periodic validation
    this.tokenValidationTimer = setInterval(() => {
      try {
        this.validateTokenPeriodically();
      } catch (error) {
        // Log the error but don't let it propagate to avoid unhandled exceptions in timer callbacks
        console.error('Error during periodic token validation:', error);
      }
    }, this.TOKEN_VALIDATION_INTERVAL);
  }

  /**
   * Performs periodic token validation with API call
   */
  private validateTokenPeriodically(): void {
    if (!this.isConnected() || this.isTokenExpired()) {
      // Clear all stored data when authentication is no longer valid
      this.clearStoredData();
      this.updateConnectionInfo({
        isConnected: false,
        error: 'GitHub token has expired'
      });
      return;
    }

    // Optional: Make periodic API call to verify token is still valid
    // This is less frequent than checking expiry time
    if (this.getAccessToken()) {
      // We could add a lightweight API call here if needed
      // For now, we rely primarily on expiry time checking
    }
  }

  private clearStoredData(): void {
    // Clear validation timer
    if (this.tokenValidationTimer) {
      clearInterval(this.tokenValidationTimer);
      this.tokenValidationTimer = null;
    }

    // Clear temporary OAuth data from sessionStorage
    sessionStorage.removeItem(this.STORAGE_KEYS.CODE_VERIFIER);
    sessionStorage.removeItem(this.STORAGE_KEYS.STATE);
    sessionStorage.removeItem(this.STORAGE_KEYS.RETURN_URL);

    // Clear persistent data from cookies
    this.clearAllGitHubTokens();
  }
}
